<template>
  <b-form
    v-if="initialised"
    @submit.prevent="onSubmit"
    @keydown="form.errors.clear($event.target.name)"
  >
    <b-card no-body>
      <b-tabs pills card vertical class="vertical">
        <b-tab title="General">
          <form-text v-model="form.name" name="name">Brand Name</form-text>
          <form-text v-model="form.business_entity_name" name="business_entity_name" />
          <form-text v-model="form.abn" name="abn">ABN</form-text>
          <form-text v-model="form.website_url" name="website_url">Website URL</form-text>
          <form-text v-model="form.gmb_listing_url" name="gmb_listing_url">
            Default GMB Listing URL
          </form-text>
          <form-textarea v-model="form.prompt_text" name="prompt_text"></form-textarea>

          <div class="form-group row" :class="{ 'invalid-wrapper': form.errors.has('send_sms') }">
            <div class="col-3 col-form-label text-right">Send SMS</div>
            <div class="col-8">
              <core-switch
                v-model="form.send_sms"
                variant="3d"
                color="primary"
                @change="form.errors.clear('send_sms')"
              />

              <small
                v-if="form.errors.has('send_sms')"
                class="invalid-feedback"
                v-text="form.errors.get('send_sms')"
              ></small>
            </div>
          </div>

          <div class="form-group row" :class="{ 'invalid-wrapper': form.errors.has('send_pn') }">
            <div class="col-3 col-form-label text-right">Send PN</div>
            <div class="col-8">
              <core-switch
                v-model="form.send_pn"
                variant="3d"
                color="primary"
                @change="form.errors.clear('send_pn')"
              />

              <small
                v-if="form.errors.has('send_pn')"
                class="invalid-feedback"
                v-text="form.errors.get('send_pn')"
              ></small>
            </div>
          </div>

          <div
            class="form-group row"
            :class="{ 'invalid-wrapper': form.errors.has('job_assignable') }"
          >
            <div class="col-3 col-form-label text-right">Job Assignable</div>
            <div class="col-8">
              <core-switch
                v-model="form.job_assignable"
                variant="3d"
                color="primary"
                @change="form.errors.clear('job_assignable')"
              />

              <small
                v-if="form.errors.has('job_assignable')"
                class="invalid-feedback"
                v-text="form.errors.get('job_assignable')"
              ></small>
            </div>
          </div>

          <div class="form-row mb-3">
            <label class="col-3 col-form-label text-right">Logo</label>
            <div class="col-8 uploader-button">
              <file-uploader
                :file-url="form.logo"
                :image-preview="true"
                @uploaded="saveUpload($event, 'logo')"
              />
              <small class="text-muted mt-2 d-block">
                Used in Pay Invoice and Accept Quote public page (in a circle style).<br />
                (Should be same width and height, recommended width 500px x 500px)
              </small>
            </div>
          </div>

          <div class="form-row mb-3">
            <label class="col-3 col-form-label text-right">Logo Small</label>
            <div class="col-8 uploader-button">
              <file-uploader
                :file-url="form.logo_small"
                :image-preview="true"
                @uploaded="saveUpload($event, 'logo_small')"
              />
              <small class="text-muted mt-2 d-block">
                Used in Job List, only shown if business has more than one brand.<br />
                (Recommended width 500px x 96 for retina)
              </small>
            </div>
          </div>

          <div class="form-row mb-3">
            <label class="col-3 col-form-label text-right">Logo Horizontal</label>
            <div class="col-8 uploader-button">
              <file-uploader
                :file-url="form.logo_horizontal"
                :image-preview="true"
                @uploaded="saveUpload($event, 'logo_horizontal')"
              />
              <small class="text-muted mt-2 d-block">
                Used for Invoices and Emails<br />
                (Recommended height 170px for retina)
              </small>
            </div>
          </div>
        </b-tab>

        <b-tab title="Contact">
          <b-card>
            <template #header>Contact</template>

            <form-text v-model="form.phone" name="phone"></form-text>
            <form-email v-model="form.email" name="email"></form-email>
            <form-email v-model="form.billing_email" name="billing_email"></form-email>
            <form-email v-model="form.email_from" name="email_from"
              >'From' Email Address</form-email
            >
          </b-card>

          <b-card>
            <template #header>Office Location</template>

            <form-text v-model="form.street" name="street"></form-text>
            <form-text v-model="form.city" name="city"></form-text>
            <form-text v-model="form.state" name="state"></form-text>
            <form-text v-model="form.postcode" name="postcode"></form-text>
          </b-card>

          <b-card>
            <template #header>Socials</template>

            <form-text v-model="form.social_instagram" name="Instagram"></form-text>
            <form-text v-model="form.social_facebook" name="Facebook"></form-text>
          </b-card>
        </b-tab>

        <b-tab title="Service Types" :title-item-class="errorTabClass('service_type')">
          <table class="table table-striped table-hover" style="max-width: 1200px">
            <thead>
              <tr>
                <th style="max-width: 200px">Name</th>
                <th style="max-width: 80px">Duration (Hours)</th>
                <th>Prompt Text</th>
                <th style="max-width: 80px">Avg Cost</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(service_type, index) in crud.data.service_types" :key="index">
                <td style="max-width: 200px">
                  <input v-model="service_type.id" type="hidden" />
                  <input
                    v-model.trim="service_type.name"
                    type="text"
                    class="form-control form-control-sm"
                    required="required"
                    :class="{ 'is-invalid': form.errors.has('service_type.name') }"
                    @input="validateDuplicateServiceTypeNames"
                  />
                </td>
                <td style="max-width: 80px">
                  <input
                    v-model.trim="service_type.duration"
                    type="number"
                    step="0.5"
                    min="0"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': form.errors.has(`${index}.duration`) }"
                    required="required"
                    @keydown="form.errors.clear()"
                  />
                </td>
                <td>
                  <textarea
                    v-model.trim="service_type.prompt_text"
                    class="form-control form-control-sm"
                    style="height: 65px"
                    @keydown="form.errors.clear()"
                  />
                </td>
                <td style="max-width: 80px">
                  <input
                    v-model.trim="service_type.avg_cost"
                    type="number"
                    step="1"
                    min="0"
                    class="form-control form-control-sm text-right"
                    :class="{ 'is-invalid': form.errors.has(`${index}.avg_cost`) }"
                    @keydown="form.errors.clear()"
                  />
                </td>
                <td>
                  <font-awesome-icon
                    :icon="['fal', 'times']"
                    class="cursor"
                    @click.stop="deleteServiceType(index)"
                  />
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="5">
                  <div v-if="form.errors.has('service_type.name')" class="invalid-wrapper">
                    <div class="invalid-feedback mb-2">{{ serviceTypeNameDuplicateMsg }}</div>
                  </div>

                  <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    @click.stop="addServiceType()"
                  >
                    <font-awesome-icon :icon="['fal', 'plus-circle']" />
                    Add Service Type
                  </button>
                </td>
              </tr>
            </tfoot>
          </table>
        </b-tab>

        <b-tab title="Invoice Items" :title-item-class="errorTabClass('invoice_items')">
          <table class="table table-striped table-hover" style="max-width: 1200px">
            <thead>
              <tr>
                <th style="max-width: 200px">Title</th>
                <th>Description</th>
                <th style="max-width: 80px">Cost</th>
                <th>Related Service</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(invoice_item, index) in crud.data.invoice_items" :key="index">
                <td style="max-width: 200px">
                  <input v-model="invoice_item.id" type="hidden" />
                  <input
                    v-model.trim="invoice_item.title"
                    type="text"
                    class="form-control form-control-sm"
                    required="required"
                    :class="{ 'is-invalid': form.errors.has('invoice_item.title') }"
                    @input="validateDuplicateInvoiceTitles"
                  />
                  <div
                    class="text-12 text-right"
                    :class="invoice_item?.title?.length > 52 ? 'text-red' : 'text-muted'"
                  >
                    {{ invoice_item?.title?.length || 0 }} / 52
                  </div>
                </td>
                <td>
                  <textarea
                    v-model.trim="invoice_item.description"
                    type="text"
                    class="form-control form-control-sm"
                    style="height: 47px"
                  />
                  <div
                    class="text-12 text-right"
                    :class="invoice_item?.description?.length > 122 ? 'text-red' : 'text-muted'"
                  >
                    {{ invoice_item?.description?.length || 0 }} / 122
                  </div>
                </td>
                <td style="max-width: 80px">
                  <input
                    v-model.trim="invoice_item.cost"
                    type="number"
                    step="0.01"
                    class="form-control form-control-sm text-right"
                    :class="{ 'is-invalid': form.errors.has('invoice_item.cost') }"
                    @keydown="form.errors.clear()"
                  />
                </td>
                <td>
                  <select v-model="invoice_item.service_type_id" class="form-control">
                    <option :value="null">Select Service Type</option>
                    <option
                      v-for="(service_type, index2) in crud.data.service_types"
                      :key="`${index}-${index2}`"
                      :value="service_type.id"
                    >
                      {{ service_type.name }}
                    </option>
                  </select>
                  <div
                    v-if="
                      invoice_item.service_type_id &&
                      !crud.data.service_types.find((st) => st.id === invoice_item.service_type_id)
                    "
                    class="text-13 text-red mt-1 pl-1"
                  >
                    Related service deleted
                  </div>
                </td>
                <td>
                  <font-awesome-icon
                    :icon="['fal', 'times']"
                    class="cursor"
                    @click.stop="deleteInvoiceItem(index)"
                  />
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="5">
                  <div v-if="form.errors.has('invoice_item.title')" class="invalid-wrapper">
                    <div class="invalid-feedback mb-2">{{ invoiceTitlesDuplicateMsg }}</div>
                  </div>

                  <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    @click.stop="addInvoiceItem()"
                  >
                    <font-awesome-icon :icon="['fal', 'plus-circle']" />
                    Add Invoice Item
                  </button>
                </td>
              </tr>
            </tfoot>
          </table>

          <div v-if="crud.getAction() === 'edit'">
            <csv-upload
              title="Invoice Item"
              :brand-id="crud.data.id"
              :endpoint="`brands/${crud.data.id}/invoice-item-csv`"
              :example-csv="`Charge,This is an example invoice line,100.00,Ant Spray\nMisc,Another type of invoice line,50.00,Misc`"
              @uploaded="init(false)"
            >
              <b class="mb-2 d-block">CSV File Format</b>
              <ul>
                <li>Each row should have three columns: Title, Description and Cost</li>
                <li>Title: text, max length 128</li>
                <li>Description: text, optional</li>
                <li>Cost: decimal numbers</li>
                <li>Related Service: the Name of the existing predefined brand "Service Types"</li>
              </ul>
            </csv-upload>
          </div>
        </b-tab>

        <b-tab title="Templates">
          <b-tabs card class="card">
            <b-tab title="Email">
              <div class="form-row mb-3">
                <label class="col-3 col-form-label text-right"> Email Header Image<br /> </label>
                <div class="col-8 mb-3 uploader-button">
                  <file-uploader
                    :file-url="form.email_header_image"
                    :image-preview="true"
                    image-alignment-class="text-left"
                    button-alignment-class="text-left"
                    submit-alignment-class="text-left"
                    display-image-width="400px"
                    cropper-width="400px"
                    @uploaded="saveUpload($event, 'email_header_image')"
                  />
                  <small class="text-muted mt-2 d-block">
                    (Recommended width 1200px, reasonable height)
                  </small>
                </div>
              </div>

              <div class="form-row mb-3">
                <label class="col-3 col-form-label text-right"> Email Footer Image<br /> </label>
                <div class="col-8 mb-3 uploader-button">
                  <file-uploader
                    :file-url="form.email_footer_image"
                    :image-preview="true"
                    image-alignment-class="text-left"
                    button-alignment-class="text-left"
                    submit-alignment-class="text-left"
                    display-image-width="400px"
                    cropper-width="400px"
                    @uploaded="saveUpload($event, 'email_footer_image')"
                  />
                  <small class="text-muted mt-2 d-block"
                    >(Recommended width 1200px, reasonable height)</small
                  >
                </div>
              </div>

              <form-text v-model="form.email_footer_color" name="email_footer_color">
                Email Footer Color
              </form-text>
            </b-tab>

            <b-tab title="Invoice PDF">
              <form-textarea v-model="form.bank_account" name="bank_account"></form-textarea>

              <form-text v-model="form.terms_and_conditions_url" name="terms_and_conditions_url">
                <template #note>
                  <small class="text-muted mt-2 d-block">
                    Used as link in invoice pdf footer and email footer
                  </small>
                </template>
              </form-text>

              <hr style="margin-bottom: 30px" />

              <form-text v-model="form.invoice_quote_promo_url" name="invoice_quote_promo_url">
                Promo PDF Image URL (Quotes)
                <template #note>
                  <small class="text-muted mt-2 d-block">
                    Fallback to <b>Promo PDF Image URL (Invoices)</b> if not set
                  </small>
                </template>
              </form-text>

              <div class="row">
                <div class="col">
                  <div class="form-row mb-3">
                    <label class="col-3 col-form-label text-right">Promo PDF Image (Quotes)</label>
                    <div class="col-8 uploader-button">
                      <file-uploader
                        :file-url="form.invoice_quote_promo_pdf_image"
                        :image-preview="true"
                        @uploaded="saveUpload($event, 'invoice_quote_promo_pdf_image')"
                      />
                      <small class="text-muted mt-2 d-block">
                        Fallback to <b>Promo PDF Image (Invoices)</b> if not set<br />
                        (Recommended dimension 1165px x 1655px)
                      </small>
                    </div>
                  </div>
                </div>
                <div class="col">
                  <div class="form-row mb-3">
                    <label class="col-3 col-form-label text-right">Footer Image (Quotes)</label>
                    <div class="col-8 uploader-button">
                      <file-uploader
                        :file-url="form.invoice_quote_footer_image"
                        :image-preview="true"
                        @uploaded="saveUpload($event, 'invoice_quote_footer_image')"
                      />
                      <small class="text-muted mt-2 d-block">
                        (Recommended dimension 1110px x 290px)
                      </small>
                      <small class="text-muted mt-2 d-block">
                        Fallback to <b>Footer Image (Invoices)</b> if not set
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <hr style="margin-bottom: 30px" />

              <form-text v-model="form.invoice_invoice_promo_url" name="invoice_invoice_promo_url">
                Promo PDF Image URL (Invoices)
              </form-text>

              <div class="row">
                <div class="col">
                  <div class="form-row mb-3">
                    <label class="col-3 col-form-label text-right"
                      >Promo PDF Image (Invoices)</label
                    >
                    <div class="col-8 uploader-button">
                      <file-uploader
                        :file-url="form.invoice_invoice_promo_pdf_image"
                        :image-preview="true"
                        @uploaded="saveUpload($event, 'invoice_invoice_promo_pdf_image')"
                      />
                      <small class="text-muted mt-2 d-block">
                        (Recommended dimension 1165px x 1655px)
                      </small>
                    </div>
                  </div>
                </div>
                <div class="col">
                  <div class="form-row mb-3">
                    <label class="col-3 col-form-label text-right">Footer Image (Invoices)</label>
                    <div class="col-8 uploader-button">
                      <file-uploader
                        :file-url="form.invoice_invoice_footer_image"
                        :image-preview="true"
                        @uploaded="saveUpload($event, 'invoice_invoice_footer_image')"
                      />
                      <small class="text-muted mt-2 d-block">
                        (Recommended dimension 1110px x 290px)
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </b-tab>

            <b-tab title="Contact Customer">
              <templates-contact
                :contact-template-data="contactCustomerTemplateData"
                :brand-id="crud.data.id"
                target="customer"
                @change="updateContactTemplates"
              />
            </b-tab>

            <b-tab title="Contact User">
              <templates-contact
                :contact-template-data="contactUserTemplateData"
                :brand-id="crud.data.id"
                target="user"
                @change="updateContactTemplates"
              />
            </b-tab>

            <b-tab title="Appointment">
              <b-card>
                <b-card-header>Shortcodes</b-card-header>
                <b-card-body class="pb-2">
                  <code>[image_link url="TARGET_URL" thumbnail="IMAGE_URL"]</code><br />
                  <br />
                  <code>[sender_first_name]</code><br />
                  <code>[sender_full_name]</code><br />
                  <code>[recipient_first_name]</code><br />
                  <code>[recipient_full_name]</code><br />
                  <br />
                  <code>[job_number]</code><br />
                  <code>[job_address]</code><br />
                  <code>[job_brand_name]</code><br />
                  <code>[job_contact_first_name]</code><br />
                  <code>[job_contact_full_name]</code><br />
                  <br />
                  <code>[billing_contact_first_name]</code><br />
                  <code>[billing_contact_full_name]</code><br />
                  <br />
                  <code>[appointment_creator_first_name]</code><br />
                  <code>[appointment_creator_full_name]</code><br />
                  <br />
                  <code>[appointment_user_first_name]</code> (technician's first name)<br />
                  <code>[appointment_user_full_name]</code> (technician's first name)<br />
                  <br />
                  <code>[appointment_date_short]</code> (e.g. 20/1/2020)<br />
                  <code>[appointment_date]</code> (e.g. Saturday 21st October)<br />
                  <code>[appointment_length]</code> (in hours)<br />
                  <code>[appointment_period]</code> (appointment start time and end time, e.g.
                  4:30pm and 6:00pm)<br />
                  <code>[appointment_time]</code> (start time, e.g. 4:30pm)<br />
                  <code>[appointment_time_fuzzy]</code> (between appointment_time and
                  appointment_time+1h, e.g. between 4:30pm and 5:30pm)<br />
                  <code>[appointment_calendar_link]</code> Add the appointment calendar file (ics)
                  to user's own calendar (appointment creation and reminder 1day & 7day emails
                  only)<br />
                  <br />
                  <code>[arrival_time]</code> (arrival at, e.g. between 3:45pm and 4:00pm, travel
                  notification only)<br />
                  <code>[arrival_estimate]</code> (arrival travel time estimate, e.g. 40 minutes,
                  travel notification only)<br />
                  <code>[travel_map]</code> (URL to the travel map, travel notification only)<br />
                </b-card-body>
              </b-card>
              <table class="table table-striped table-hover">
                <thead>
                  <tr>
                    <th style="width: 350px">Name</th>
                    <th>Template</th>
                  </tr>
                </thead>
                <tbody v-if="appointmentTemplateData.length">
                  <tr
                    v-for="(entry, index) in appointmentTemplateData"
                    :key="`appointment-template-${index}`"
                  >
                    <td>
                      {{ entry.title }}
                      <p v-if="entry.subtitle" class="mt-1">
                        <small class="text-muted text-13">
                          {{ entry.subtitle }}
                        </small>
                      </p>
                    </td>
                    <td>
                      <input
                        v-if="entry.type && entry.type === 'text'"
                        v-model="entry.template"
                        class="form-control"
                      />
                      <template v-else>
                        <textarea
                          v-model="entry.template"
                          class="form-control form-control-sm"
                          rows="10"
                        />
                        <text-count
                          :text="entry.template"
                          :max="160"
                          class="text-muted text-right"
                        />
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </b-tab>
            <b-tab title="Invoice">
              <b-card>
                <b-card-header> Shortcodes </b-card-header>
                <b-card-body class="pb-2">
                  <code>[image_link url="TARGET_URL" thumbnail="IMAGE_URL"]</code><br />
                  <br />
                  <code>[sender_first_name]</code><br />
                  <code>[sender_full_name]</code><br />
                  <code>[recipient_first_name]</code><br />
                  <code>[recipient_full_name]</code><br />
                  <br />
                  <code>[job_number]</code><br />
                  <code>[job_address]</code><br />
                  <code>[job_brand_name]</code><br />
                  <code>[job_contact_first_name]</code><br />
                  <code>[job_contact_full_name]</code><br />
                  <br />
                  <code>[billing_contact_first_name]</code><br />
                  <code>[billing_contact_full_name]</code><br />
                  <br />
                  <code>[invoice_id]</code><br />
                  <code>[invoice_uuid]</code><br />
                  <code>[invoice_number]</code><br />
                  <code>[invoice_total]</code><br />
                  <code>[invoice_due_date]</code><br />
                  <code>[invoice_pdf_url]</code><br />
                  <code>[invoice_landing_url]</code><br />
                  <code>[invoice_user_first_name]</code> (invoice technician's first name)<br />
                  <code>[invoice_user_last_name]</code> (invoice technician's last name)<br />
                  <br />
                  <code>[invoice_opt_in_link]</code><br />
                  <br />
                  <code>[contextual_links_to_failed_quotes]</code><br />
                </b-card-body>
              </b-card>

              <table class="table table-striped table-hover">
                <thead>
                  <tr>
                    <th style="width: 350px">Name</th>
                    <th>Template</th>
                  </tr>
                </thead>
                <tbody v-if="invoiceTemplateData.length">
                  <tr
                    v-for="(entry, index) in invoiceTemplateData"
                    :key="`invoice-template-${index}`"
                  >
                    <td>
                      {{ entry.title }}
                      <p v-if="entry.subtitle" class="mt-1">
                        <small class="text-muted text-13">
                          {{ entry.subtitle }}
                        </small>
                      </p>
                    </td>
                    <td>
                      <input
                        v-if="entry.type && entry.type === 'text'"
                        v-model="entry.template"
                        class="form-control"
                      />
                      <template v-else>
                        <textarea
                          v-model="entry.template"
                          class="form-control form-control-sm"
                          rows="10"
                        />
                        <text-count
                          :text="entry.template"
                          :max="160"
                          class="text-muted text-right"
                        />
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </b-tab>
          </b-tabs>
        </b-tab>

        <b-tab title="Fees">
          <div class="row">
            <div class="col">
              <b-card>
                <template #header>
                  <strong>Brand Fees</strong>
                  Brand
                </template>

                <form-text
                  v-model.trim="form.core_compliance_admin_discount"
                  name="core_compliance_admin_discount"
                  :is-number="true"
                  min="0"
                  max="100"
                  step="0.1"
                >
                  <template #append>
                    <span class="input-group-text">%</span>
                  </template>
                </form-text>
                <form-text
                  v-model.trim="form.referral_commission"
                  name="referral_commission"
                  :is-number="true"
                  min="0"
                  max="100"
                  step="0.1"
                >
                  <template #append>
                    <span class="input-group-text">%</span>
                  </template>
                </form-text>
                <form-text
                  v-model.trim="form.card_fee"
                  name="card_fee"
                  :is-number="true"
                  min="0"
                  max="100"
                  step="0.01"
                >
                  <template #append>
                    <span class="input-group-text">%</span>
                  </template>
                </form-text>
                <form-text
                  v-model.trim="form.admin_fee"
                  name="admin_fee"
                  :is-number="true"
                  min="0"
                  step="0.01"
                >
                  <template #prepend>
                    <span class="input-group-text">$</span>
                  </template>
                </form-text>
                <form-text
                  v-model.trim="form.marketing_fee"
                  name="marketing_fee"
                  :is-number="true"
                  min="0"
                  step="0.01"
                >
                  <template #prepend>
                    <span class="input-group-text">$</span>
                  </template>
                </form-text>
              </b-card>
            </div>
            <div class="col">
              <b-card>
                <template #header>
                  <strong>Brand Fees</strong>
                  Contractor and Franchisee Commissions
                </template>

                <form-text
                  v-model.trim="form.contractor_job_commission"
                  name="contractor_job_commission"
                  :is-number="true"
                  min="0"
                  max="100"
                  step="0.1"
                >
                  <template #append>
                    <span class="input-group-text">%</span>
                  </template>
                </form-text>
                <form-text
                  v-model.trim="form.contractor_booking_commission"
                  name="contractor_booking_commission"
                  :is-number="true"
                  min="0"
                  max="100"
                  step="0.1"
                >
                  <template #append>
                    <span class="input-group-text">%</span>
                  </template>
                </form-text>
                <form-text
                  v-model.trim="form.franchisee_job_commission"
                  name="franchisee_job_commission"
                  :is-number="true"
                  min="0"
                  max="100"
                  step="0.1"
                >
                  <template #append>
                    <span class="input-group-text">%</span>
                  </template>
                </form-text>
                <form-text
                  v-model.trim="form.franchisee_booking_commission"
                  name="franchisee_booking_commission"
                  :is-number="true"
                  min="0"
                  max="100"
                  step="0.1"
                >
                  <template #append>
                    <span class="input-group-text">%</span>
                  </template>
                </form-text>
              </b-card>
            </div>
            <div class="col">
              <b-card>
                <template #header>
                  <strong>Brand Fees</strong>
                  Appointment Compliance Bonus
                </template>

                <form-text
                  v-model.trim="form.appointment_compliance_note"
                  name="appointment_compliance_note"
                  :is-number="true"
                  min="0"
                  max="100"
                  step="0.1"
                >
                  <template #append>
                    <span class="input-group-text">%</span>
                  </template>
                </form-text>
                <form-text
                  v-model.trim="form.appointment_compliance_picture"
                  name="appointment_compliance_picture"
                  :is-number="true"
                  min="0"
                  max="100"
                  step="0.1"
                >
                  <template #append>
                    <span class="input-group-text">%</span>
                  </template>
                </form-text>
                <form-text
                  v-model.trim="form.appointment_compliance_invoice"
                  name="appointment_compliance_invoice"
                  :is-number="true"
                  min="0"
                  max="100"
                  step="0.1"
                >
                  <template #append>
                    <span class="input-group-text">%</span>
                  </template>
                </form-text>
                <form-text
                  v-model.trim="form.appointment_compliance_payment"
                  name="appointment_compliance_payment"
                  :is-number="true"
                  min="0"
                  max="100"
                  step="0.1"
                >
                  <template #append>
                    <span class="input-group-text">%</span>
                  </template>
                </form-text>
              </b-card>
            </div>
          </div>
        </b-tab>

        <b-tab title="Xero">
          <b-card>
            <template #header>Xero Settings</template>

            <form-select
              v-if="allXeroAppOptions.length > 1"
              v-model="form.xeros.xero_app_id"
              name="Connect Xero App"
              :options="allXeroAppOptions"
              :single-row="true"
            >
            </form-select>

            <div v-else class="form-component-row-single form-row">
              <label class="form-component-label col-3 col-form-label mb-2 text-right">
                Connect Xero App
              </label>
              <div class="form-component-field col-8 mb-3">
                <button
                  v-if="!$lodash.get(crud.data, 'xeros.xero_app_id')"
                  type="button"
                  class="mt-2 btn btn-primary btn-sm"
                  @click.stop="connectXero()"
                >
                  <font-awesome-icon :icon="['fal', 'plus-circle']" class="mr-1" />
                  Connect Xero
                </button>

                <small class="text-muted mt-2 d-block">
                  <template v-if="$lodash.get(crud.data, 'xeros.xero_app_id')">
                    To reconnect, first select "Disconnect and Remove Xero App" and save
                  </template>
                  <template v-else-if="allXeroAppOptions.length > 1">
                    Select an existing Xero App, or click "Connect Xero" button to connect to a new
                    Xero App
                  </template>
                  <template v-else>
                    Click "Connect Xero" button to connect to a new Xero App
                  </template>
                </small>
              </div>
            </div>

            <form-text v-model="form.xeros.bank_account_id" name="bank_account_id"></form-text>
            <form-text v-model="form.xeros.cash_account_id" name="cash_account_id"></form-text>
            <form-text v-model="form.xeros.account_code" name="account_code"></form-text>

            <div class="form-component-row-single form-row">
              <label class="form-component-label col-3 col-form-label mb-2 text-right">
                WARNING
              </label>
              <div class="form-component-field col-8 mb-3" style="margin-top: 7px">
                <b class="text-red">
                  Please make sure the settings for Xero are correct, otherwise Xero integration
                  will not work correctly!!!
                </b>
              </div>
            </div>
          </b-card>
        </b-tab>

        <b-tab title="Payment Gateway">
          <form-bootstrap-radio
            v-if="!$store.getters['payment-gateway/isSingleGateway']()"
            v-model="form.payment_gateway"
            name="gateway"
            :options="['Stripe', 'PayChoice']"
          />
          <form-text
            v-if="form.payment_gateway === 'Stripe'"
            v-model="form.stripe_secret"
            name="stripe_secret"
          />
          <template v-if="form.payment_gateway === 'PayChoice'">
            <form-text v-model="form.paychoice_username" name="paychoice_username" />
            <form-text v-model="form.paychoice_password" name="paychoice_password" />
          </template>

          <div
            class="form-group row"
            :class="{ 'invalid-wrapper': form.errors.has('disable_amex') }"
          >
            <div class="col-3 col-form-label text-right">Disable AMEX</div>
            <div class="col-8">
              <core-switch
                v-model="form.disable_amex"
                variant="3d"
                color="primary"
                @change="form.errors.clear('disable_amex')"
              />

              <small
                v-if="form.errors.has('disable_amex')"
                class="invalid-feedback"
                v-text="form.errors.get('disable_amex')"
              ></small>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </b-card>

    <form-validation-alert :errors="form.errors" />

    <b-card-footer class="mb-5">
      <button class="btn btn-primary" :disabled="form.notReady()">
        <font-awesome-icon v-if="form.busy" spin :icon="['fal', 'spinner']" />
        <font-awesome-icon v-else :icon="['fal', 'check']" />
        {{ crud.getAction() === "create" ? "Create" : "Save" }}
      </button>
    </b-card-footer>
  </b-form>
</template>

<script type="text/babel">
import orderBy from "lodash/orderBy";
import ux from "../../mixins/ux";
import FileUploader from "../../components/common/FileUploader.vue";
import TextCount from "../../components/common/TextCount";
import TemplatesContact from "@/views/brands/partials/TemplatesContact.vue";
import CsvUpload from "@/views/brands/partials/CsvUpload.vue";

export default {
  components: {
    FileUploader,
    TemplatesContact,
    TextCount,
    CsvUpload,
  },

  mixins: [ux],

  data() {
    return {
      currentTemplates: {},
      appointmentTemplateData: [],
      invoiceTemplateData: [],
      contactTemplateData: [],

      allXeroApps: [],
      allXeroAppOptions: [],

      initialised: false,
      crud: null,

      form: new window.AppForm({
        business_id: 1,
        name: "",
        business_entity_name: "",
        logo: "",
        logo_small: "",
        logo_horizontal: "",
        abn: "",
        website_url: "",
        gmb_listing_url: "",
        street: "",
        city: "",
        state: "",
        postcode: "",
        email: "",
        billing_email: "",
        phone: "",
        email_from: "",
        email_header_image: "",
        email_footer_image: "",
        email_footer_color: "",
        bank_account: "",
        terms_and_conditions_url: "",
        service_types: [],
        invoice_items: [],
        templates: {},
        contractor_job_commission: 0,
        contractor_booking_commission: 0,
        franchisee_job_commission: 0,
        franchisee_booking_commission: 0,
        appointment_compliance_note: 0,
        appointment_compliance_picture: 0,
        appointment_compliance_invoice: 0,
        appointment_compliance_payment: 0,
        core_compliance_admin_discount: 0,
        referral_commission: 0,
        card_fee: 0,
        admin_fee: 0,
        marketing_fee: 0,

        disable_amex: false,
        send_sms: true,
        send_pn: true,
        job_assignable: false,

        payment_gateway: this.$store.getters["payment-gateway/defaultGateway"](),
        stripe_secret: "",
        paychoice_username: "",
        paychoice_password: "",
        prompt_text: "",

        social_instagram: "",
        social_facebook: "",
        xeros: {
          bank_account_id: "",
          cash_account_id: "",
          account_code: "",
          xero_app_id: null,
        },

        invoice_quote_footer_image: "",
        invoice_invoice_footer_image: "",
        invoice_quote_promo_pdf_image: "",
        invoice_invoice_promo_pdf_image: "",
        invoice_quote_promo_url: "",
        invoice_invoice_promo_url: "",
      }),
      serviceTypeNameDuplicateMsg: "Service type names must be unique for a brand.",
      invoiceTitlesDuplicateMsg: "Invoice line item titles must be unique for a brand.",
    };
  },

  computed: {
    contactCustomerTemplateData() {
      return this.contactTemplateData.filter((template) => template.target === "customer");
    },

    contactUserTemplateData() {
      return this.contactTemplateData.filter((template) => template.target === "user");
    },
  },

  created() {
    this.init();
  },

  methods: {
    init(reset) {
      if (reset) {
        this.initialised = false;
      }

      this.$http.get("/xero/all-apps").then((response) => {
        this.allXeroApps = response.data;
      });

      this.crud = new window.AppCrud("brand");

      this.crud.initFormData(this.form).then(() => {
        // Browser Title
        if (this.$lodash.get(this.crud, "data.name")) {
          // noinspection JSUnresolvedFunction
          this.$root.setTitle(`Brand - ${this.$lodash.get(this.crud, "data.name")}`);
        }

        this.initAllTemplates();

        this.crud.data.service_types = orderBy(this.crud.data.service_types, ["name"], ["asc"]);

        this.form.xeros.xero_app_id = this.$lodash.get(this.crud, "data.xeros.xero_app_id");
        this.allXeroAppOptions = this.allXeroApps.map((xeroApp) => ({
          id: xeroApp.id,
          name: `${xeroApp.xero_organisation} connected by ${xeroApp.xero_email}`,
        }));

        this.allXeroAppOptions.unshift({
          id: this.form.xeros.xero_app_id ? 0 : null,
          name: this.form.xeros.xero_app_id
            ? "Disconnect and Remove Xero App"
            : "Select Xero App...",
        });

        this.initialised = true;
      });
    },

    initAllTemplates() {
      // Store current templates in currentTemplates object
      this.currentTemplates = {};
      if (this.crud.getAction() !== "create") {
        this.crud.data.templates.forEach((entry) => {
          this.currentTemplates[
            `${entry.channel}_${entry.category}_${entry.name}_${entry.target}`
          ] = {
            id: entry.id,
            template: entry.template,
          };
        });
      }

      // Initialise templateData arrays
      this.appointmentTemplateData = [];
      this.appointmentTemplateData = this.$store.state.brands.templateConfigs.appointment.map(
        (entry) => this.initTemplate(entry)
      );
      this.invoiceTemplateData = [];
      this.invoiceTemplateData = this.$store.state.brands.templateConfigs.invoice.map((entry) =>
        this.initTemplate(entry)
      );
      this.contactTemplateData = [];
      if (this.crud.data && this.crud.data.templates) {
        this.contactTemplateData = this.crud.data.templates.filter(
          (entry) => entry.category === "contact"
        );
      }
    },

    initTemplate(entry) {
      const path = `${entry.channel}_${entry.category}_${entry.name}_${entry.target}`;

      const base = {
        id: this.currentTemplates[path]?.id || null,
        brand_id: this.$lodash.get(this.crud, "data.id", null),
        template: this.currentTemplates[path]?.template || "",
      };

      return {
        ...base,
        ...entry,
      };
    },

    saveUpload(url, type) {
      this.form[type] = url;
    },

    onSubmit() {
      this.form.service_types = this.crud.data.service_types;
      this.form.templates = this.appointmentTemplateData.concat(
        this.invoiceTemplateData,
        this.contactTemplateData
      );

      this.crud.submit(this.form).then((response) => {
        if (this.crud.getAction() === "create") {
          window.toastr.success("Brand created!", "Success");

          this.$router.push(
            {
              name: "brands.edit",
              params: { id: response.id },
            },
            () => {
              this.crud.initFormData(this.form).then(() => {
                this.init();
              });
            }
          );
        } else {
          window.toastr.success("Brand updated!", "Success");

          this.crud.initFormData(this.form).then(() => {
            this.init();
          });
        }
      });
    },

    addServiceType() {
      this.crud.data.service_types.push({
        id: "",
        name: "",
        duration: "",
      });

      this.form.errors.clear();
    },

    deleteServiceType(index) {
      this.crud.data.service_types.splice(index, 1);

      this.form.errors.clear();
    },

    validateDuplicateServiceTypeNames() {
      let names = this.$lodash.map(this.form.service_types, "name");
      names = names.map((name) => name.toLowerCase());

      if (this.$lodash.uniq(names).length !== names.length) {
        this.form.errors.add("service_type.name", this.serviceTypeNameDuplicateMsg);
      } else {
        this.form.errors.clear();
      }
    },

    validateDuplicateInvoiceTitles() {
      let titles = this.$lodash.map(this.form.invoice_items, "title");
      titles = titles.map((title) => title.toLowerCase());

      if (this.$lodash.uniq(titles).length !== titles.length) {
        this.form.errors.add("invoice_item.title", this.invoiceTitlesDuplicateMsg);
      } else {
        this.form.errors.clear();
      }
    },

    addInvoiceItem() {
      this.crud.data.invoice_items.push({
        id: "",
        title: "",
        cost: 0,
      });

      this.form.errors.clear();
    },

    deleteInvoiceItem(index) {
      this.crud.data.invoice_items.splice(index, 1);

      this.form.errors.clear();
    },

    updateContactTemplates(change) {
      if (change.target && change.templates) {
        if (change.target === "customer") {
          this.contactTemplateData = this.contactUserTemplateData.concat(change.templates);
          return;
        }

        if (change.target === "user") {
          this.contactTemplateData = this.contactCustomerTemplateData.concat(change.templates);
        }
      }
    },

    connectXero() {
      const alertTitle = this.$lodash.get(this.crud.data, "xeros.xero_app.access_token")
        ? "Reconnect to Xero"
        : "Connect to Xero";
      const alertText = this.$lodash.get(this.crud.data, "xeros.xero_app.access_token")
        ? 'Existing connection will be removed!!!\n\n You may need to remove "Connected Apps" in Xero.'
        : "You will leave BOLT to grant Xero organisation access";

      window
        .sweetAlert({
          title: alertTitle,
          text: alertText,
          icon: "warning",
          dangerMode: false,
          buttons: {
            cancel: "No",
            confirm: "Yes",
          },
        })
        .then((confirmed) => {
          if (confirmed) {
            this.$http.put("/xero/connect", { brand_id: this.crud.data.id }).then((response) => {
              if (response.status === 200) {
                window.location = response.body.url;
              } else {
                window.toastr.error(
                  "Something went wrong when trying to establish Xero connect",
                  "Error"
                );
              }
            });
          }
        });
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
::v-deep .file-uploader__preview,
::v-deep .file-uploader__preview img,
::v-deep .file-uploader__edit-image {
  max-height: 150px;
}
</style>
